import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { DocumentStatus } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../api';
import { AppState } from '../../../../../store/models/app-state';
import {
    InvoiceGenericRequestItem,
    InvoicePositionsRequest,
    InvoiceProductRequestItem,
} from '../../models/invoice-positions-request';
import { InvoiceTextItem } from '../../../models/invoice-text-item';
import { PositionsFormValues } from '../../models/positions-form-values';
import { StepData } from '../../../../../common/components/new-stepper/step-data';
import { isInvoiceGenericItem, isInvoiceProductItem, isInvoiceTextItem } from '../../../models/invoice-item';

export const INVOICE_DETAIL_POSITIONS_SUBMIT = 'INVOICE_DETAIL_POSITIONS_SUBMIT';
export const INVOICE_DETAIL_POSITIONS_FETCH = 'INVOICE_DETAIL_POSITIONS_FETCH';
export const INVOICE_DETAIL_POSITIONS_RECEIVE = 'INVOICE_DETAIL_POSITIONS_RECEIVE';
export const INVOICE_DETAIL_POSITIONS_SAVE = 'INVOICE_DETAIL_POSITIONS_SAVE';
export const INVOICE_DETAIL_POSITIONS_SAVE_ERROR = 'INVOICE_DETAIL_POSITIONS_SAVE_ERROR';

export const submitPositions = (data: StepData<PositionsFormValues>) => ({
    type: INVOICE_DETAIL_POSITIONS_SUBMIT,
    data,
});

export const fetchPositions = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const invoiceId = getState().invoiceDetail.overview.stepData.values.id;
        if (!invoiceId) {
            return;
        }

        const response = await api.invoices.get(invoiceId);
        if (response.success) {
            dispatch({ type: INVOICE_DETAIL_POSITIONS_RECEIVE, invoice: response.result });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

/** Moves a line of a sent invoice to an interchangeable account (#2326 R-239). */
export const changeCounterAccount = (itemId: number, taxRateId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const invoiceId = getState().invoiceDetail.overview.stepData.values.id;
        if (!invoiceId) {
            return;
        }

        const response = await api.invoices.changeCounterAccount(invoiceId, itemId, taxRateId);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.invoices.positions.counterAccountChanged')));
            // Re-read rather than take the answer: only the read says whether the invoice went to BMD meanwhile.
            await fetchPositions()(dispatch, getState, { api });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.invoices.positions.counterAccountNotChanged')));
        }
    };
};

export const savePositions = (positionDetails: {
    netShippingCosts: number;
    netSurcharge: number;
    isTaxFree: boolean;
}) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const invoice = getState().invoiceDetail.positions.stepData.values as Partial<PositionsFormValues>;
        const invoiceId = getState().invoiceDetail.overview.stepData.values.id;
        // Nothing to hang the positions on. Leaving silently let the caller read
        // this as a failed save and report one without a request ever going out
        // (#2210).
        if (!invoiceId) {
            return false;
        }

        // Completed and canceled invoices are immutable; a save fired from a
        // stale view must not reach the positions endpoint anymore.
        const status = getState().invoiceDetail.overview.stepData.values.status;
        if (DocumentStatus.Created !== status) {
            return true;
        }

        const { allItems, ..._rest } = invoice;

        // An unloaded positions step must not be sent: the backend treats an
        // empty items array as "delete all positions".
        if (!allItems) {
            return true;
        }

        dispatch({ type: INVOICE_DETAIL_POSITIONS_SAVE });
        const productRequestItems: InvoiceProductRequestItem[] =
            allItems?.filter(isInvoiceProductItem).map(item => {
                const { taxRate, getDescription: _getDescription, ...data } = item;
                return { ...data, taxRateId: taxRate?.id ?? null };
            }) ?? [];
        const textItems = (allItems?.filter(isInvoiceTextItem) as unknown as InvoiceTextItem[]) ?? [];
        const surchargeRequestItems: InvoiceGenericRequestItem[] =
            allItems?.filter(isInvoiceGenericItem).map(item => {
                const { taxRate, ...data } = item;
                return { ...data, taxRateId: taxRate.id };
            }) ?? [];

        const request: InvoicePositionsRequest = {
            id: invoiceId,
            items: productRequestItems,
            textItems: textItems,
            surchargeItems: surchargeRequestItems,
            netSurcharge: positionDetails.netSurcharge,
            netShippingCosts: positionDetails.netShippingCosts,
            isTaxFree: positionDetails.isTaxFree,
        };

        const response = await api.invoices.updatePositions(request);

        if (response.success) {
            dispatch({ type: INVOICE_DETAIL_POSITIONS_RECEIVE, invoice: response.result });
        } else {
            dispatch({ type: INVOICE_DETAIL_POSITIONS_SAVE_ERROR });
            return false;
        }

        return true;
    };
};
